





























































































































































import { Component, Vue, Prop } from "vue-property-decorator";
import moment from "moment";
import hotkeys from "hotkeys-js";

const IS_MUTED = "player_is_muted";
const VOLUME = "player_volume";

@Component
export default class VideoPlayer extends Vue {
  @Prop(String) src!: string;
  @Prop(Number) duration!: number;
  @Prop({ default: null }) poster!: string | null;
  @Prop() markers!: { _id: string; name: string; time: number }[];
  @Prop({ default: null }) preview!: string | null;

  videoNotice = "";
  noticeTimeout: null | number = null;
  previewX = 0;
  progress = 0;
  buffered = null as any;
  isPlaying = false;
  showPoster = true;

  isVolumeDragging = false;
  isMuted = localStorage.getItem(IS_MUTED) === "true";
  volume = parseFloat(localStorage.getItem(VOLUME) ?? "1");
  hideControlsTimeoutDuration = 3000;
  hideControlsTimeout: null | number = null;
  hideControls = false;

  volumeIncrementPercentage = 0.05;

  paniced = false;

  mounted() {
    const vid = <HTMLVideoElement>this.$refs.video;
    if (vid) {
      vid.volume = this.volume;
      vid.muted = this.isMuted;
    }
    window.addEventListener("mouseup", this.onVolumeMouseUp);

    hotkeys("space", this.focusedTogglePlay);
    hotkeys("up", this.focusedIncrementVolume);
    hotkeys("down", this.focusedDecrementVolume);
  }

  beforeDestroy() {
    window.removeEventListener("mouseup", this.onVolumeMouseUp);

    hotkeys.unbind("space", this.focusedTogglePlay);
    hotkeys.unbind("up", this.focusedIncrementVolume);
    hotkeys.unbind("down", this.focusedDecrementVolume);
  }

  panic() {
    this.paniced = true;
    this.pause();
    const vid = <HTMLVideoElement>this.$refs.video;
    if (vid) {
      vid.src = "";
    }
    window.location.replace(localStorage.getItem("pm_panic") || "https://google.com");
  }

  formatTime(secs: number) {
    return moment().startOf("day").seconds(secs).format("H:mm:ss");
  }

  currentProgress() {
    return this.progress;
  }

  get imageIndex() {
    return Math.floor(this.previewX * 100);
  }

  startControlsTimeout() {
    if (this.hideControlsTimeout) {
      window.clearTimeout(this.hideControlsTimeout);
    }
    this.hideControls = false;
    this.hideControlsTimeout = window.setTimeout(() => {
      this.hideControls = true;
    }, this.hideControlsTimeoutDuration);
  }

  async toggleFullscreen() {
    const videoWrapper = this.$refs.videoWrapper as HTMLElement & {
      mozRequestFullScreen?(): Promise<void>;
      webkitRequestFullscreen?(): Promise<void>;
      msRequestFullscreen?(): Promise<void>;
    };

    if (!videoWrapper) return;

    if (document.fullscreenElement && document.fullscreenElement === videoWrapper) {
      document.exitFullscreen();
    } else {
      const requestFullscreen =
        videoWrapper.requestFullscreen ||
        videoWrapper.webkitRequestFullscreen ||
        videoWrapper.mozRequestFullScreen ||
        videoWrapper.msRequestFullscreen;
      if (requestFullscreen) {
        try {
          // Invoke function with element context
          await requestFullscreen.call(videoWrapper);
          // Focus the wrapper when in fullscreen, to allow
          // for focus dependant keyboard shortcuts
          videoWrapper.focus();
        } catch (err) {
          // Browser refused fullscreen for some reason, do nothing
        }
      }
    }
  }

  setVolume(volume: number, notice = false) {
    this.startControlsTimeout();

    const vid = <HTMLVideoElement>this.$refs.video;
    if (vid) {
      if (volume <= 0.02) {
        this.mute();
      } else {
        if (volume > 1) {
          volume = 1;
        }
        if (notice) this.notice(`Volume: ${(volume * 100).toFixed(0)}%`);

        this.unmute();
        this.volume = volume;
        localStorage.setItem(VOLUME, volume.toString());
        vid.volume = volume;
      }
    }
  }

  onVolumeClick(ev: any) {
    const volumeBar = document.getElementById("volume-bar");
    if (volumeBar) {
      const rect = volumeBar.getBoundingClientRect();
      const y = (ev.clientY - rect.bottom) * -1;
      const yPercentage = y / rect.height;
      this.setVolume(yPercentage);
    }
  }

  onVolumeMouseDown() {
    this.isVolumeDragging = true;
  }

  onVolumeMouseUp() {
    this.isVolumeDragging = false;
  }

  onVolumeDrag(ev) {
    if (this.isVolumeDragging) {
      this.onVolumeClick(ev);
    }
  }

  onMouseMove(ev) {
    const progressBar = document.getElementById("progress-bar");
    if (progressBar) {
      const rect = progressBar.getBoundingClientRect();
      const x = ev.clientX - rect.left;
      this.previewX = x / rect.width;
    }
  }

  percentOfVideo(time: number) {
    return time / this.duration;
  }

  get progressPercent() {
    return this.percentOfVideo(this.progress);
  }

  seekRel(delta: number, text?: string) {
    this.startControlsTimeout();
    this.notice(`Seek: ${delta > 0 ? "+" : ""}${delta.toString()}s`);

    this.seek(Math.min(this.duration, Math.max(0, this.progress + delta)), text);
  }

  seek(time: number, text?: string, play = false) {
    const vid = <HTMLVideoElement>this.$refs.video;
    if (vid) {
      vid.currentTime = time;

      if (play) this.play();

      if (text) {
        this.notice(text);
      }
    }
  }

  onProgressClick(ev: any) {
    const progressBar = document.getElementById("progress-bar");
    if (progressBar) {
      const rect = progressBar.getBoundingClientRect();
      const x = ev.clientX - rect.left;
      const xPercentage = x / rect.width;
      this.seek(xPercentage * this.duration, "", true);
    }
  }

  notice(text: string, duration = 1500) {
    if (this.noticeTimeout) {
      clearTimeout(this.noticeTimeout);
    }
    this.videoNotice = text;
    this.noticeTimeout = window.setTimeout(() => {
      this.videoNotice = "";
    }, duration);
  }

  play(notice = false) {
    const vid = <HTMLVideoElement>this.$refs.video;
    if (vid) {
      if (notice) this.notice("Play");

      vid.play();
      this.isPlaying = true;
      this.showPoster = false;
      vid.ontimeupdate = (ev) => {
        this.progress = vid.currentTime;
        this.buffered = vid.buffered;
      };
      this.$emit("play");
    }
  }

  isPaused() {
    const vid = <HTMLVideoElement>this.$refs.video;
    return vid && vid.paused;
  }

  pause(notice = false) {
    const vid = <HTMLVideoElement>this.$refs.video;
    if (vid) {
      if (notice) this.notice("Paused");

      vid.pause();
      this.isPlaying = false;
    }
  }

  isVideoFocused() {
    const videoWrapper = <Element>this.$refs.videoWrapper;
    return (
      videoWrapper &&
      document.activeElement &&
      (document.activeElement === videoWrapper || videoWrapper.contains(document.activeElement))
    );
  }

  focusedTogglePlay(ev: KeyboardEvent) {
    if (this.isVideoFocused()) {
      ev.preventDefault(); // prevent page scroll
      this.togglePlay(true);
    }
  }

  focusedIncrementVolume(ev: KeyboardEvent) {
    if (this.isVideoFocused()) {
      ev.preventDefault(); // prevent page scroll
      this.setVolume(this.volume + this.volumeIncrementPercentage, true);
    }
  }

  focusedDecrementVolume(ev: KeyboardEvent) {
    if (this.isVideoFocused()) {
      ev.preventDefault(); // prevent page scroll
      this.setVolume(this.volume - this.volumeIncrementPercentage, true);
    }
  }

  togglePlay(notice = false) {
    this.startControlsTimeout();

    const vid = <HTMLVideoElement>this.$refs.video;
    if (vid) {
      if (vid.paused) {
        this.play(notice);
      } else {
        this.pause(notice);
      }
    }
  }

  mute(notice = false) {
    const vid = <HTMLVideoElement>this.$refs.video;
    if (vid) {
      if (notice) this.notice("Muted");

      vid.muted = true;
      this.isMuted = true;
      localStorage.setItem(IS_MUTED, "true");
    }
  }

  unmute(notice = false) {
    const vid = <HTMLVideoElement>this.$refs.video;
    if (vid) {
      if (notice) this.notice("Unmuted");

      vid.muted = false;
      this.isMuted = false;
      localStorage.setItem(IS_MUTED, "false");
    }
  }

  toggleMute(notice = false) {
    this.startControlsTimeout();

    const vid = <HTMLVideoElement>this.$refs.video;
    if (vid) {
      if (vid.muted) {
        this.unmute(notice);
      } else {
        this.mute(notice);
      }
    }
  }
}
